import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { useCreatePackaging } from '../../hooks/useCreatePackaging';
import { useUpdatePackaging } from '../../hooks/useUpdatePackaging';
import { useActiveRates } from '../../hooks/useActiveRates';
import { CreateOwnBoxRatePrice, PackagingCategory } from '../../models/Packaging';
import { PackagingItem } from '../../models/Packaging';
import { usePackagingBoxGroups } from '../../hooks/usePackagingBoxGroups';
import { useAuthStore } from '@shared/stores/authStore';
import { useCreatePackagingBoxGroup } from '../../hooks/useCreatePackagingBoxGroup';
import { RatePricesTable } from '../interaction/RatePricesTable';
import { PackagingDimensionsFields } from '@modules/Packaging/shared/components/PackagingDimensionsFields';
import { EMPTY_DIMENSION_FORM } from '@modules/Packaging/shared/types/dimensions';
import type { PackagingDimensionFormState } from '@modules/Packaging/shared/types/dimensions';
import {
  buildPackagingDimensionsPayload,
  dimensionFormFromItem,
} from '@modules/Packaging/shared/utils/packagingDimensions';
import { cn } from '@shared/utils/utils';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';

type ModalMode = 'create' | 'edit';
type CreateStep = 'details' | 'rates';

const CREATE_STEPS: CreateStep[] = ['details', 'rates'];

const CreateStepIndicator = ({ currentStep }: { currentStep: CreateStep }) => {
  const { t } = useTranslation('materialAlmacenModule');
  const currentIndex = CREATE_STEPS.indexOf(currentStep);

  return (
    <div className="flex items-center w-full mb-6" data-testid="warehouse-material-create-step-indicator">
      {CREATE_STEPS.map((step, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;

        return (
          <div key={step} className="flex items-center flex-1 last:flex-none min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-colors',
                  isCompleted && 'bg-primary border-primary text-primary-foreground',
                  isCurrent && 'border-primary bg-primary/10 text-primary',
                  !isCompleted && !isCurrent && 'border-border bg-muted/40 text-muted-foreground',
                )}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : index + 1}
              </div>
              <span
                className={cn(
                  'hidden truncate text-sm transition-colors sm:inline',
                  isCurrent ? 'font-semibold text-foreground' : 'text-muted-foreground',
                )}
              >
                {t(`modal.create.steps.${step}`)}
              </span>
            </div>
            {index < CREATE_STEPS.length - 1 && (
              <div
                className={cn(
                  'mx-3 h-0.5 flex-1 rounded-full transition-colors',
                  index < currentIndex ? 'bg-primary' : 'bg-border',
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export interface PackagingModalsRef {
  openCreateModal: () => void;
  openEditModal: (item: PackagingItem) => void;
}

interface PackagingModalsProps {
  onRefetch?: () => void;
}

const PackagingModals = forwardRef<PackagingModalsRef, PackagingModalsProps>(({ onRefetch }, ref) => {
  const createModalRef = useRef<ModalCenterRef>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation('materialAlmacenModule');

  // Estado del formulario de creación
  const [mode, setMode] = useState<ModalMode>('create');
  const [editingId, setEditingId] = useState<number | null>(null);
  // En edición conservamos la categoría del ítem (own_box / marketing_item); en alta siempre own_box.
  const [editingCategory, setEditingCategory] = useState<PackagingCategory | null>(null);
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [ratePrices, setRatePrices] = useState<Record<number, number>>({});
  const [ratePriceErrors, setRatePriceErrors] = useState<Record<number, string>>({});
  const [ratesError, setRatesError] = useState<string | null>(null);
  const [selectedBoxGroupId, setSelectedBoxGroupId] = useState<number | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [existingImageName, setExistingImageName] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<PackagingDimensionFormState>(EMPTY_DIMENSION_FORM);
  const [initialDimensions, setInitialDimensions] = useState<PackagingDimensionFormState>(EMPTY_DIMENSION_FORM);
  const [createStep, setCreateStep] = useState<CreateStep>('details');
  const team = useAuthStore((state) => state.team);
  const { createBoxGroupAsync, isCreatingBoxGroup } = useCreatePackagingBoxGroup();

  const { activeRates, isLoading: isLoadingRates } = useActiveRates(mode === 'create');

  const { createPackaging, isCreating } = useCreatePackaging({
    onSuccess: () => {
      createModalRef.current?.closeModal();
      resetForm();
      onRefetch?.();
    },
  });

  const { updatePackaging, isUpdating } = useUpdatePackaging({
    onSuccess: () => {
      createModalRef.current?.closeModal();
      resetForm();
      onRefetch?.();
    },
  });

  const resetForm = () => {
    setMode('create');
    setEditingId(null);
    setEditingCategory(null);
    setName('');
    setSku('');
    setRatePrices({});
    setRatePriceErrors({});
    setRatesError(null);
    setSelectedBoxGroupId(null);
    setImageFile(null);
    setExistingImageName(null);
    setExistingImageUrl(null);
    setPreviewImageUrl(null);
    setDimensions(EMPTY_DIMENSION_FORM);
    setInitialDimensions(EMPTY_DIMENSION_FORM);
    setCreateStep('details');
  };

  const getImageNameFromUrl = (imageUrl: string | null): string | null => {
    if (!imageUrl) return null;
    const rawName = imageUrl.split('/').pop();
    if (!rawName) return null;
    return decodeURIComponent(rawName);
  };

  // Material de almacén: sin selector de cliente; grupos de cajas del propio almacén.
  const warehouseTeamId = team?.id ?? null;
  const { boxGroups, isLoading: isBoxGroupsLoading } = usePackagingBoxGroups({
    enabled: true,
  });

  const isBoxCategory = mode === 'create' || editingCategory === 'own_box' || editingCategory === 'customer_box';
  const isBoxGroupRequiredAndMissing = !selectedBoxGroupId;
  const isCreatingBoxGroupLoading = isCreatingBoxGroup;

  const initializeRatePrices = useCallback(() => {
    const initial: Record<number, number> = {};
    for (const rate of activeRates) {
      initial[rate.id] = 0;
    }
    setRatePrices(initial);
    setRatePriceErrors({});
    setRatesError(null);
  }, [activeRates]);

  useEffect(() => {
    if (mode === 'create' && activeRates.length > 0) {
      initializeRatePrices();
    }
  }, [mode, activeRates, initializeRatePrices]);

  const handleRatePriceChange = useCallback((rateId: number, value: string) => {
    const parsed = value.trim() === '' ? NaN : parseFloat(value);
    setRatePrices((prev) => ({
      ...prev,
      [rateId]: Number.isNaN(parsed) ? 0 : parsed,
    }));
    setRatePriceErrors((prev) => {
      const next = { ...prev };
      delete next[rateId];
      return next;
    });
    setRatesError(null);
  }, []);

  const areRatePricesValid =
    mode !== 'create' ||
    activeRates.length === 0 ||
    activeRates.every((rate) => {
      const price = ratePrices[rate.id];
      return price !== undefined && !Number.isNaN(price) && price >= 0;
    });

  const isStep1Valid = Boolean(name && sku && !isBoxGroupRequiredAndMissing);

  const handleGoToRatesStep = () => {
    if (!isStep1Valid) {
      return;
    }

    if (activeRates.length === 0) {
      setRatesError(t('modal.create.rates_empty'));
      return;
    }

    setRatesError(null);
    setCreateStep('rates');
  };

  const handleBackToDetailsStep = () => {
    setRatesError(null);
    setCreateStep('details');
  };

  const handleSubmit = () => {
    const category: PackagingCategory = mode === 'edit' && editingCategory ? editingCategory : 'own_box';

    if (mode === 'create' && category === 'own_box') {
      if (activeRates.length === 0) {
        setRatesError(t('modal.create.rates_empty'));
        return;
      }

      const priceErrors: Record<number, string> = {};
      for (const rate of activeRates) {
        const price = ratePrices[rate.id];
        if (price === undefined || Number.isNaN(price) || price < 0) {
          priceErrors[rate.id] = t('validation.rate_price_invalid');
        }
      }

      if (Object.keys(priceErrors).length > 0) {
        setRatePriceErrors(priceErrors);
        setRatesError(t('validation.rates_required'));
        return;
      }
    }

    setRatesError(null);

    const ratesPayload: CreateOwnBoxRatePrice[] | undefined =
      mode === 'create' && category === 'own_box'
        ? activeRates.map((rate) => ({
            rate_id: rate.id,
            price: ratePrices[rate.id] ?? 0,
          }))
        : undefined;

    const dimensionsPayload = buildPackagingDimensionsPayload(dimensions, category, mode, initialDimensions);

    const payload = {
      name,
      sku,
      category,
      ...(ratesPayload ? { rates: ratesPayload } : {}),
      ...(selectedBoxGroupId ? { packaging_box_group_id: selectedBoxGroupId } : {}),
      ...(imageFile ? { image_hash: imageFile } : {}),
      ...dimensionsPayload,
    };

    if (mode === 'edit' && editingId) {
      updatePackaging(editingId, payload);
      return;
    }

    createPackaging(payload);
  };

  const handleCreateBoxGroup = async (inputName: string) => {
    if (!warehouseTeamId) {
      return;
    }

    const createdGroup = await createBoxGroupAsync({
      name: inputName,
      teamId: warehouseTeamId,
    });

    setSelectedBoxGroupId(createdGroup.id);
  };

  useImperativeHandle(ref, () => ({
    openCreateModal: () => {
      resetForm();
      createModalRef.current?.openModal();
    },
    openEditModal: (item: PackagingItem) => {
      resetForm();
      setMode('edit');
      setEditingId(item.id);
      setName(item.name);
      setSku(item.code);
      setEditingCategory(item.category === 'marketing_item' || item.category === 'own_box' ? item.category : 'own_box');
      setSelectedBoxGroupId(item.packaging_box_group_id ?? null);
      setExistingImageName(getImageNameFromUrl(item.image_url));
      setExistingImageUrl(item.image_url);
      const loadedDimensions = dimensionFormFromItem(
        item.folded_dimensions,
        item.assembled_dimensions,
        item.dimension_unit,
      );
      setDimensions(loadedDimensions);
      setInitialDimensions(loadedDimensions);
      createModalRef.current?.openModal();
    },
  }));

  useEffect(() => {
    if (!imageFile) {
      setPreviewImageUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(imageFile);
    setPreviewImageUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [imageFile]);

  const currentImageSrc = previewImageUrl || existingImageUrl;

  const renderCreateDetailsStep = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2 md:col-span-2">
        <label className="text-sm font-medium text-text mb-1 block">{t('modal.create.box_group_label')}</label>
        <Combobox
          options={boxGroups.map((group) => ({
            label: group.name,
            value: String(group.id),
          }))}
          value={selectedBoxGroupId ? String(selectedBoxGroupId) : ''}
          setValue={(value) => setSelectedBoxGroupId(value ? parseInt(value, 10) : null)}
          placeholder={t('modal.create.box_group_placeholder')}
          disabled={isBoxGroupsLoading || isCreatingBoxGroupLoading}
          className="w-full"
          dataTestId="warehouse-material-box-group-select"
          onCreateOption={handleCreateBoxGroup}
          createOptionLabel={(input) =>
            input
              ? t('modal.create.box_group_create_option', { value: input })
              : t('modal.create.box_group_create_option_idle')
          }
          isCreatingOption={isCreatingBoxGroup}
        />
        <p className="text-xs text-subtext leading-snug">{t('modal.create.box_group_helper')}</p>
      </div>

      <div>
        <label className="text-sm font-medium text-text mb-1 block">{t('modal.create.name_label')}</label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('modal.create.name_placeholder')}
          disabled={isCreatingBoxGroupLoading}
          data-testid="modal-crear-pack-title"
        />
      </div>

      <div>
        <label className="text-sm font-medium text-text mb-1 block">{t('modal.create.sku_label')}</label>
        <Input
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          placeholder={t('modal.create.sku_placeholder')}
          disabled={isCreatingBoxGroupLoading}
          data-testid="packaging-sku-input"
        />
      </div>

      {isBoxCategory && (
        <PackagingDimensionsFields
          state={dimensions}
          onChange={setDimensions}
          disabled={isCreatingBoxGroupLoading}
          labels={{
            sectionTitle: t('modal.create.dimensions_section_title'),
            foldedTitle: t('modal.create.dimensions_folded_title'),
            assembledTitle: t('modal.create.dimensions_assembled_title'),
            length: t('modal.create.dimensions_length'),
            width: t('modal.create.dimensions_width'),
            height: t('modal.create.dimensions_height'),
            unitCm: t('modal.create.dimensions_unit_cm'),
            unitMt: t('modal.create.dimensions_unit_mt'),
          }}
        />
      )}

      <div>
        <label className="text-sm font-medium text-text mb-1 block">{t('modal.create.image_label')}</label>
        <input
          ref={imageInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.gif,.bmp,.webp"
          className="hidden"
          disabled={isCreatingBoxGroupLoading}
          onChange={(e) => {
            const selectedFile = e.target.files?.[0] ?? null;
            setImageFile(selectedFile);
            if (selectedFile) {
              setExistingImageName(null);
            }
          }}
          data-testid="packaging-image-input"
        />
        <div className="border border-card-border rounded-md bg-body-bg">
          <div className="flex items-center justify-between gap-2 h-10">
            <span className="text-sm text-subtext truncate pl-3 pr-3">
              {imageFile?.name || t('modal.create.image_placeholder')}
            </span>
            <div className="flex items-center h-full shrink-0">
              {imageFile && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setImageFile(null);
                    if (imageInputRef.current) {
                      imageInputRef.current.value = '';
                    }
                  }}
                  disabled={isCreatingBoxGroupLoading}
                  data-testid="packaging-image-clear-button"
                  className="h-full rounded-none border-0 border-l border-card-border px-3"
                >
                  {t('modal.create.image_remove')}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => imageInputRef.current?.click()}
                disabled={isCreatingBoxGroupLoading}
                data-testid="packaging-image-select-button"
                className="h-full rounded-none border-0 border-l border-card-border px-3"
              >
                {t('modal.create.image_select')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCreateRatesStep = () => (
    <div className="space-y-2">
      <label className="text-sm font-medium text-text mb-1 block">{t('modal.create.rates_label')} *</label>
      <p className="text-xs text-subtext leading-snug">{t('modal.create.rates_helper')}</p>
      {isLoadingRates ? (
        <p className="text-sm text-subtext">{t('modal.create.rates_loading')}</p>
      ) : (
        <RatePricesTable
          rates={activeRates}
          prices={ratePrices}
          onPriceChange={handleRatePriceChange}
          errors={ratePriceErrors}
        />
      )}
      {ratesError && <p className="text-xs text-error mt-1">{ratesError}</p>}
    </div>
  );

  const renderEditForm = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2 md:col-span-2">
        <label className="text-sm font-medium text-text mb-1 block">{t('modal.create.box_group_label')}</label>
        <Combobox
          options={boxGroups.map((group) => ({
            label: group.name,
            value: String(group.id),
          }))}
          value={selectedBoxGroupId ? String(selectedBoxGroupId) : ''}
          setValue={(value) => setSelectedBoxGroupId(value ? parseInt(value, 10) : null)}
          placeholder={t('modal.create.box_group_placeholder')}
          disabled={isBoxGroupsLoading || isCreatingBoxGroupLoading}
          className="w-full"
          dataTestId="warehouse-material-box-group-select"
          onCreateOption={handleCreateBoxGroup}
          createOptionLabel={(input) =>
            input
              ? t('modal.create.box_group_create_option', { value: input })
              : t('modal.create.box_group_create_option_idle')
          }
          isCreatingOption={isCreatingBoxGroup}
        />
        <p className="text-xs text-subtext leading-snug">{t('modal.create.box_group_helper')}</p>
      </div>

      <div>
        <label className="text-sm font-medium text-text mb-1 block">{t('modal.create.name_label')}</label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('modal.create.name_placeholder')}
          disabled={isCreatingBoxGroupLoading}
          data-testid="modal-crear-pack-title"
        />
      </div>

      <div>
        <label className="text-sm font-medium text-text mb-1 block">{t('modal.create.sku_label')}</label>
        <Input
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          placeholder={t('modal.create.sku_placeholder')}
          disabled={isCreatingBoxGroupLoading}
          data-testid="packaging-sku-input"
        />
      </div>

      {isBoxCategory && (
        <PackagingDimensionsFields
          state={dimensions}
          onChange={setDimensions}
          disabled={isCreatingBoxGroupLoading}
          labels={{
            sectionTitle: t('modal.create.dimensions_section_title'),
            foldedTitle: t('modal.create.dimensions_folded_title'),
            assembledTitle: t('modal.create.dimensions_assembled_title'),
            length: t('modal.create.dimensions_length'),
            width: t('modal.create.dimensions_width'),
            height: t('modal.create.dimensions_height'),
            unitCm: t('modal.create.dimensions_unit_cm'),
            unitMt: t('modal.create.dimensions_unit_mt'),
          }}
        />
      )}

      <div>
        <label className="text-sm font-medium text-text mb-1 block">{t('modal.create.image_label')}</label>
        <input
          ref={imageInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.gif,.bmp,.webp"
          className="hidden"
          disabled={isCreatingBoxGroupLoading}
          onChange={(e) => {
            const selectedFile = e.target.files?.[0] ?? null;
            setImageFile(selectedFile);
            if (selectedFile) {
              setExistingImageName(null);
            }
          }}
          data-testid="packaging-image-input"
        />
        <div className="border border-card-border rounded-md bg-body-bg p-3">
          <div className="flex items-center justify-between gap-3">
            <div
              className="w-10 h-10 rounded-md border border-card-border overflow-hidden bg-body-bg flex items-center justify-center"
              data-testid="packaging-image-preview"
            >
              {currentImageSrc ? (
                <img
                  src={currentImageSrc}
                  alt={imageFile?.name || existingImageName || t('modal.create.image_label')}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-[10px] text-subtext">{t('modal.create.image_placeholder')}</span>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => imageInputRef.current?.click()}
              disabled={isCreatingBoxGroupLoading}
              data-testid="packaging-image-select-button"
            >
              Cambiar imagen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCreateFooter = () => {
    if (createStep === 'details') {
      return (
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => createModalRef.current?.closeModal()}
            data-testid="packaging-cancel-button"
          >
            {t('button.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleGoToRatesStep}
            disabled={!isStep1Valid || isCreatingBoxGroupLoading}
            data-testid="warehouse-material-create-next-button"
          >
            {t('button.next')}
            <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between gap-2">
        <Button
          variant="outline"
          onClick={handleBackToDetailsStep}
          disabled={isCreating}
          data-testid="warehouse-material-create-back-button"
        >
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          {t('button.back')}
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => createModalRef.current?.closeModal()}
            data-testid="packaging-cancel-button"
          >
            {t('button.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isCreating || !areRatePricesValid || activeRates.length === 0}
            data-testid="packaging-save-button"
          >
            {isCreating ? t('button.creating') : t('button.create')}
          </Button>
        </div>
      </div>
    );
  };

  const renderEditFooter = () => (
    <div className="flex justify-end gap-2">
      <Button
        variant="outline"
        onClick={() => createModalRef.current?.closeModal()}
        data-testid="packaging-cancel-button"
      >
        {t('button.cancel')}
      </Button>
      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={isUpdating || !name || !sku || isBoxGroupRequiredAndMissing}
        data-testid="packaging-save-button"
      >
        {isUpdating ? t('button.updating') : t('button.update')}
      </Button>
    </div>
  );

  return (
    <>
      {/* Modal de creación de material */}
      <ModalCenter
        ref={createModalRef}
        title={mode === 'edit' ? t('modal.edit.title') : t('modal.create.title')}
        width="xl"
        data-testid="modal-crear-material-almacen"
        onCloseModal={resetForm}
        footer={mode === 'create' ? renderCreateFooter() : renderEditFooter()}
      >
        <div className="space-y-4" data-testid="modal-crear-material-almacen-content">
          {mode === 'create' && <CreateStepIndicator currentStep={createStep} />}
          {mode === 'create' && createStep === 'details' && renderCreateDetailsStep()}
          {mode === 'create' && createStep === 'rates' && renderCreateRatesStep()}
          {mode === 'edit' && renderEditForm()}
          {mode === 'create' && ratesError && createStep === 'details' && (
            <p className="text-xs text-error">{ratesError}</p>
          )}
        </div>
      </ModalCenter>
    </>
  );
});

PackagingModals.displayName = 'PackagingModals';

export default PackagingModals;
